import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SupervisorRegistration.css';
import { supabase } from '../../lib/supabaseClient';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { validatePasswordStrength, getPasswordErrorMessage } from '../../lib/passwordValidation';

const SupervisorRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [offices, setOffices] = useState<string[]>([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    office: '',
    role: '',
    email: '',
    password: ''
  });

  // Calculate password strength
  const passwordStrength = validatePasswordStrength(formData.password);

  // Fetch offices from office_deployment table
  useEffect(() => {
    const fetchOffices = async () => {
      setOfficesLoading(true);
      try {
        const { data, error } = await supabase
          .from('office_deployment')
          .select('office')
          .order('office', { ascending: true });

        if (error) {
          console.error('Error fetching offices:', error);
          return;
        }

        // Extract unique office names
        if (data && data.length > 0) {
          const officeNames = data.map((item: any) => item.office).filter((office: any) => office);
          const uniqueOffices = [...new Set(officeNames)];
          setOffices(uniqueOffices);
        }
      } catch (error) {
        console.error('Error fetching offices:', error);
      } finally {
        setOfficesLoading(false);
      }
    };

    fetchOffices();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // If role is being changed to OJT Coordinator, automatically set office to "CCIT"
    if (name === 'role') {
      if (value === 'OJT Coordinator') {
        setFormData(prevState => ({
          ...prevState,
          role: value,
          office: 'CCIT'
        }));
        return;
      } else {
        // If changing from OJT Coordinator to Supervisor, clear the office field
        setFormData(prevState => ({
          ...prevState,
          role: value,
          office: prevState.role === 'OJT Coordinator' ? '' : prevState.office
        }));
        return;
      }
    }
    
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Check if office should be disabled (when role is OJT Coordinator)
  const isOfficeDisabled = formData.role === 'OJT Coordinator';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate password strength before submission
    if (!passwordStrength.isValid) {
      setError(getPasswordErrorMessage(passwordStrength));
      setLoading(false);
      setPasswordTouched(true);
      return;
    }

    try {
      // Prepare registration data for user metadata
      // Determine the actual user role based on the selection
      const userRole = formData.role === 'OJT Coordinator' ? 'ojt_coordinator' : 'supervisor';
      
      // Debug logging
      console.log('Selected role:', formData.role);
      console.log('Mapped user_role:', userRole);
      
      const registrationData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        office: formData.office.trim(),
        role_title: formData.role.trim(),
        email: formData.email.toLowerCase().trim(),
        user_role: userRole
      };
      
      // Log the data being stored to verify it
      console.log('Storing supervisor registration data:', registrationData);
      
      localStorage.setItem('pendingSupervisorRegistration', JSON.stringify(registrationData));

      // 1. Create auth user with additional metadata
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            office: formData.office,
            role_title: formData.role,
            user_role: userRole
          },
          emailRedirectTo: `${window.location.origin}/verify`
        }
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // Show success message and redirect to confirmation page
        // After email verification, user will be redirected to /verify which will then redirect to /supervisor-home
        navigate('/registration-success');
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err instanceof Error) {
        if (err.message.includes('duplicate key')) {
          setError('This email or employee ID is already registered');
        } else if (err.message.includes('email')) {
          setError('Please check your email address');
        } else if (err.message.includes('password')) {
          setError('Password must be at least 6 characters long');
        } else {
          setError(err.message);
        }
      } else {
        setError('An error occurred during registration');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    window.location.href = '/supervisor';
  };

  const goToStudentRegistration = () => {
    window.location.href = '/student-registration';
  };

  return (
    <div className="login-container">
      <div className="login-box registration-box">
        <div className="logo">
          <h1>Intern Tr<FaMapMarkerAlt className="location-icon" />ckr</h1>
          <p className="tagline">Anytime. Anywhere.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="input-row">
            <div className="input-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your first name</div>
              {formData.firstName && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, firstName: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="input-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your surname</div>
              {formData.lastName && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, lastName: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="input-row">
            <div className="input-group">
              <label>Office</label>
              <select
                name="office"
                value={formData.office}
                onChange={handleChange}
                disabled={isOfficeDisabled || officesLoading}
                required
                className={`select-input ${isOfficeDisabled ? 'disabled-input' : ''}`}
              >
                <option value="">
                  {officesLoading ? 'Loading offices...' : 'Select an office'}
                </option>
                {offices.map((office) => (
                  <option key={office} value={office}>
                    {office}
                  </option>
                ))}
              </select>
              <div className="label-description">
                {isOfficeDisabled 
                  ? 'Office is automatically set for OJT Coordinator' 
                  : 'Please select your office'}
              </div>
            </div>
            <div className="input-group">
              <label>Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="select-input"
              >
                <option value="">Select a role</option>
                <option value="Supervisor">Supervisor</option>
                <option value="OJT Coordinator">OJT Coordinator</option>
              </select>
              <div className="label-description">Please select your role</div>
            </div>
          </div>
          <div className="input-row">
            <div className="input-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your email</div>
              {formData.email && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, email: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={() => setPasswordTouched(true)}
                required
              />
              <div className="label-description">Please enter a strong password</div>
              {formData.password && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, password: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {/* Password Requirements Indicator */}
          {(formData.password || passwordTouched) && (
            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul>
                <li className={passwordStrength.hasMinLength ? 'valid' : 'invalid'}>
                  {passwordStrength.hasMinLength ? '✓' : '✗'} At least 8 characters
                </li>
                <li className={passwordStrength.hasUpperCase ? 'valid' : 'invalid'}>
                  {passwordStrength.hasUpperCase ? '✓' : '✗'} An uppercase letter (A-Z)
                </li>
                <li className={passwordStrength.hasLowerCase ? 'valid' : 'invalid'}>
                  {passwordStrength.hasLowerCase ? '✓' : '✗'} A lowercase letter (a-z)
                </li>
                <li className={passwordStrength.hasNumber ? 'valid' : 'invalid'}>
                  {passwordStrength.hasNumber ? '✓' : '✗'} A number (0-9)
                </li>
                <li className={passwordStrength.hasSpecialChar ? 'valid' : 'invalid'}>
                  {passwordStrength.hasSpecialChar ? '✓' : '✗'} A special character (!, @, #, $, etc.)
                </li>
              </ul>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          <div className="button-group">
            <button type="submit" className="login-button register-button" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </button>
          </div>
        </form>
        <div className="links-section">
          <p>Already have an account? <button onClick={goToLogin} className="link-button">Click here to login.</button></p>
          <p>Not a supervisor? <button onClick={goToStudentRegistration} className="link-button">Click here to register as a Student.</button></p>
        </div>
      </div>
    </div>
  );
};

export default SupervisorRegistration;
